import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { apiClient, formatSAR } from "../lib/api";
import { EquipmentSection } from "../components/EquipmentSection";
import { CARTAL_CONTACT } from "../lib/contactInfo";
import { GOV_LINKS } from "../lib/govLinks";
import { FEATURES } from "../lib/features";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { useFavorites } from "../lib/favorites";
import { localizeValue } from "../lib/displayValues";
import VerifiedBadge from "../components/VerifiedBadge";
import StarRating from "../components/StarRating";
import TrustScoreBadge from "../components/TrustScoreBadge";
import AuctionBidBox from "../components/AuctionBidBox";
import {
  MapPin, Gauge, Fuel, Settings, Calendar, CheckCircle2, ShieldCheck,
  Wrench, CreditCard, ArrowRight, Sparkles, Phone, MessageSquare, Heart, Loader2,
  ChevronLeft, ChevronRight, AlertTriangle, X as CloseIcon, Stethoscope,
  Pencil, Flag, FileSearch, HandCoins, EyeOff, Inbox, Check, Ban, Building2,
  Receipt, FileText, Anchor,
} from "lucide-react";
import VehicleCard from "../components/VehicleCard";
import SubmitOfferModal from "../components/SubmitOfferModal";
import ShareBar from "../components/ShareBar";
import VehicleFinancingPlans from "../components/VehicleFinancingPlans";
import useShareVisitTracker from "../lib/useShareVisitTracker";
import { rememberVehicle } from "../lib/resumeBrowsing";
import { QRCodeSVG } from "qrcode.react";
import { SALE_OPTIONS_INDEX } from "../data/saleOptions";
import Seo from "../components/Seo";

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1200&q=80";

/** Build a normalised gallery array from a vehicle doc. Old listings store
 *  just `image` (string); newer ones store `images` (array). We always return
 *  at least one URL so the UI never breaks. */
function gallery(v) {
  const arr = Array.isArray(v.images) ? v.images.filter(Boolean) : [];
  if (arr.length > 0) {
    // ensure cover image is first, no duplicates
    if (v.image && !arr.includes(v.image)) return [v.image, ...arr];
    return arr;
  }
  return [v.image || FALLBACK_IMG];
}

/** Convert an arbitrary KSA phone string into a WhatsApp-ready digits-only
 *  international number (e.g. "+966 50 123 4567" -> "966501234567"). */
function waNumber(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/\D+/g, "");
  if (!digits) return "";
  // Local "05xxxxxxxx" → "9665xxxxxxxx"
  if (digits.startsWith("05") && digits.length === 10) return "966" + digits.slice(1);
  if (digits.startsWith("5") && digits.length === 9) return "966" + digits;
  return digits;
}

export default function VehicleDetailPage() {
  const { id } = useParams();
  const { lang, t } = useI18n();
  const { user } = useAuth();
  const { isFavorite, toggle } = useFavorites();
  const navigate = useNavigate();
  const location = useLocation();
  // UTM share-visit tracking — fires once if the URL has ?utm_source=...
  useShareVisitTracker(id);
  // iter206 — Page-view tracking (independent from share_visits). Fires
  // once per session per vehicle so "Top-viewed listings" reflects real
  // openings from ALL sources (direct, search, QR, share links, etc.).
  React.useEffect(() => {
    if (!id) return;
    const flag = `cartal:view:${id}`;
    try {
      if (window.sessionStorage.getItem(flag)) return;
      window.sessionStorage.setItem(flag, "1");
    } catch { /* private-mode */ }
    apiClient.post("/analytics/vehicle-views", { vehicle_id: id }).catch(() => { /* silent */ });
  }, [id]);
  const [v, setV] = useState(null);
  const [err, setErr] = useState(null);
  const [contactBusy, setContactBusy] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  // iter222 — fullscreen lightbox for the vehicle gallery
  const [lightboxOpen, setLightboxOpen] = useState(false);
  // iter223 — thumbnail carousel ref: kept at top-level so hooks order
  // stays stable even when the component early-returns before the JSX.
  const thumbsRef = React.useRef(null);
  const [justPublishedDismissed, setJustPublishedDismissed] = useState(false);
  const [similar, setSimilar] = useState([]);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);
  const [reportDone, setReportDone] = useState(false);
  const [reportReason, setReportReason] = useState("misleading");
  const [reportNotes, setReportNotes] = useState("");
  const [qrOpen, setQrOpen] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  const [offersData, setOffersData] = useState(null); // seller-only: list of offers

  useEffect(() => {
    apiClient
      .get(`/vehicles/${id}`)
      .then(({ data }) => {
        setV(data);
        setActiveIdx(0);
        rememberVehicle(data); // continue-browsing
      })
      .catch((e) => setErr(e.response?.data?.detail || e.message));
    // Load similar listings (best-effort, never blocks the page)
    apiClient
      .get(`/vehicles/${id}/similar`)
      .then(({ data }) => setSimilar(data.items || []))
      .catch(() => setSimilar([]));
  }, [id]);

  // Phase 2A — seller-side: load offers received for this listing.
  const loadOffers = React.useCallback(async () => {
    if (!v || !user) return;
    const isSeller = v.seller_id === user.id || v.owner_id === user.id;
    if (!isSeller) { setOffersData(null); return; }
    try {
      const { data } = await apiClient.get(`/vehicles/${v.id}/offers`);
      setOffersData(data);
    } catch {
      setOffersData({ items: [], total: 0 });
    }
  }, [v, user]);

  useEffect(() => { loadOffers(); }, [loadOffers]);

  // iter334 — Legacy manual <head> mutation effect removed.
  //
  // ROOT CAUSE OF WRONG CANONICAL ON /vehicle/{id}
  //
  // The previous "per-listing preview tags" useEffect used
  // document.querySelector(...).setAttribute("content", ...) to write
  // og:url / og:title / og:description. Because <Seo/> (react-helmet-async)
  // now also emits the SAME tags, every rendered Vehicle page carried TWO
  // competing <meta property="og:url"> tags:
  //
  //   1) legacy effect wrote  …/vehicle/{id}?lang=ar
  //   2) Helmet emitted       …/vehicle/{id}
  //
  // When Googlebot's non-JS pass (or an incomplete JS render) only saw
  // the static index.html og:url = "https://cartal.sa/" OR saw the
  // query-string variant first, it folded /vehicle/{id} URLs into the
  // wrong canonical cluster.
  //
  // Fix: Helmet is now the ONLY writer of og:url / og:title /
  // og:description / canonical. The static OG tags for those properties
  // were also removed from frontend/public/index.html. og:image for
  // vehicle pages is now carried through Helmet via Seo's new ogImage
  // prop (see <Seo .../> below).

  if (err) return (
    <div className="max-w-[1400px] mx-auto p-10 text-[#525252]" data-testid="vehicle-detail-not-found">
      {/* iter332 — ensure non-existent / errored vehicle URLs never
          become indexable soft-404s. Helmet injects noindex here. */}
      <Seo
        title={lang === "ar" ? "الإعلان غير متاح | كارتال" : "Listing not available | CARTAL"}
        description={lang === "ar" ? "هذا الإعلان لم يعد متاحاً على كارتال." : "This listing is no longer available on CARTAL."}
        canonicalPath={`/vehicle/${id}`}
        noindex
      />
      {String(err)}
    </div>
  );
  if (!v) return <div className="max-w-[1400px] mx-auto p-10 text-[#6b6b6b]">{t("common.loading")}</div>;

  const images = gallery(v);
  // iter223 — thumbnail carousel: arrows scroll the inner track one
  // viewport-width at a time. overflow-hidden on the viewport guarantees
  // the track can never widen the parent grid cell.
  const THUMBS_PER_PAGE = 3;
  const scrollThumbs = (dir) => {
    const el = thumbsRef.current;
    if (!el) return;
    // One page = viewport width + one gap so the next page's first
    // thumb lands flush against the edge (thumbs are separated by a
    // gap-2 = 8px gap, so a bare clientWidth scroll would leave an
    // 8px blank strip).
    el.scrollBy({ left: dir * (el.clientWidth + 8), behavior: "smooth" });
  };
  const fav = user ? isFavorite(v.id) : false;
  const isSelf = user && (v.seller_id === user.id || v.owner_id === user.id);
  // Detect demo/test listings — force all contact routes through the
  // official CARTAL platform WhatsApp so users don't try to reach
  // fictitious sellers ("TESTMake TESTModel" etc.).
  const isDemo = Boolean(
    v.is_demo === true ||
    (typeof v.make === "string" && v.make.toUpperCase().startsWith("TEST")) ||
    (typeof v.model === "string" && v.model.toUpperCase().startsWith("TEST"))
  );
  // Official CARTAL support WhatsApp (single source of truth) — also used
  // as the fallback contact for demo listings.
  const CARTAL_WA = CARTAL_CONTACT.whatsappNumberIntl; // "966500230065"
  const phoneRaw = isDemo ? CARTAL_CONTACT.whatsappNumber : (v.contact_phone || "");
  const waRaw = isDemo ? CARTAL_WA : (v.contact_whatsapp || v.contact_phone || "");
  const phoneE164 = phoneRaw ? phoneRaw.replace(/\s+/g, "") : "";
  const waDigits = waNumber(waRaw);
  const waText = isDemo
    ? (lang === "ar"
        ? `مرحباً كارتال، لدي استفسار حول إعلان تجريبي على المنصّة.`
        : `Hi CARTAL, I have a question about a demo listing on the platform.`)
    : (lang === "ar"
        ? `مرحباً، هل ${v.year} ${v.make} ${v.model} لا تزال متاحة؟ (إعلان كارتال #${v.vin || v.id.slice(0, 8)})`
        : `Hi, is the ${v.year} ${v.make} ${v.model} still available? (CARTAL listing #${v.vin || v.id.slice(0, 8)})`);

  const contactSeller = async () => {
    if (!user) {
      navigate("/login", { state: { from: `/vehicle/${id}` } });
      return;
    }
    if (isSelf) return;
    setContactBusy(true);
    try {
      const { data } = await apiClient.post("/conversations", {
        vehicle_id: v.id,
        body:
          lang === "ar"
            ? `مرحباً، هل ${v.year} ${v.make} ${v.model} لا تزال متاحة؟`
            : `Hi, is the ${v.year} ${v.make} ${v.model} still available?`,
      });
      navigate(`/messages?c=${data.id}`);
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setContactBusy(false);
    }
  };

  const onFav = () => {
    if (!user) {
      navigate("/login", { state: { from: `/vehicle/${id}` } });
      return;
    }
    toggle(v.id);
  };

  const decideOffer = async (offerId, action) => {
    try {
      await apiClient.post(`/offers/${offerId}/${action}`, { note: "" });
      await loadOffers();
    } catch (e) {
      alert(e?.response?.data?.detail?.ar || e?.response?.data?.detail || "Failed");
    }
  };

  const nextImg = () => setActiveIdx((i) => (i + 1) % images.length);
  const prevImg = () => setActiveIdx((i) => (i - 1 + images.length) % images.length);

  const submitReport = async () => {
    if (!user) {
      navigate("/login", { state: { from: `/vehicle/${id}` } });
      return;
    }
    if (!reportNotes.trim() || reportNotes.trim().length < 10) {
      alert(lang === "ar" ? "اكتب وصفاً (10 أحرف على الأقل)." : "Write a description (min 10 chars).");
      return;
    }
    setReportBusy(true);
    try {
      await apiClient.post("/reports", {
        target_type: "vehicle",
        target_id: v.id,
        reason: reportReason,
        description: reportNotes.trim(),
      });
      setReportDone(true);
      setTimeout(() => {
        setReportOpen(false);
        setReportDone(false);
        setReportNotes("");
      }, 1500);
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setReportBusy(false);
    }
  };

  const isRental = v.type === "rental";
  const tr = (s) => localizeValue(s, lang);

  const specs = [
    { icon: Calendar, label: t("common.year"), value: v.year },
    { icon: Gauge, label: t("common.km"), value: v.mileage_km?.toLocaleString() },
    { icon: Fuel, label: lang === "ar" ? "الوقود" : "Fuel", value: tr(v.fuel) },
    { icon: Settings, label: lang === "ar" ? "ناقل الحركة" : "Transmission", value: tr(v.transmission) },
    { icon: MapPin, label: lang === "ar" ? "المدينة" : "City", value: tr(v.city) },
  ];

  // Category-specific extra specs (motorcycle / heavy truck / boat) — appended
  // to the main grid so buyers see the specialised info immediately.
  if (v.type === "motorcycle") {
    if (v.moto_engine_cc)
      specs.push({ icon: Settings, label: lang === "ar" ? "سعة المحرك" : "Engine", value: `${v.moto_engine_cc} cc` });
    if (v.moto_cylinders)
      specs.push({ icon: Settings, label: lang === "ar" ? "الأسطوانات" : "Cylinders", value: v.moto_cylinders });
    if (v.moto_cooling)
      specs.push({ icon: Settings, label: lang === "ar" ? "التبريد" : "Cooling", value: lang === "ar" ? ({ liquid: "سائل", air: "هوائي", oil: "زيتي" }[v.moto_cooling] || v.moto_cooling) : v.moto_cooling });
    if (v.moto_abs)
      specs.push({ icon: Settings, label: "ABS", value: lang === "ar" ? "متوفّر" : "Yes" });
  } else if (v.type === "heavy") {
    if (v.truck_payload_tons)
      specs.push({ icon: Gauge, label: lang === "ar" ? "الحمولة" : "Payload", value: `${v.truck_payload_tons} ${lang === "ar" ? "طن" : "t"}` });
    if (v.truck_gvw_tons)
      specs.push({ icon: Gauge, label: "GVW", value: `${v.truck_gvw_tons} ${lang === "ar" ? "طن" : "t"}` });
    if (v.truck_axles)
      specs.push({ icon: Settings, label: lang === "ar" ? "المحاور" : "Axles", value: v.truck_axles });
    if (v.truck_cargo_m3)
      specs.push({ icon: Settings, label: lang === "ar" ? "الصندوق" : "Cargo", value: `${v.truck_cargo_m3} m³` });
    if (v.truck_engine_l)
      specs.push({ icon: Settings, label: lang === "ar" ? "المحرك" : "Engine", value: `${v.truck_engine_l} L` });
    if (v.truck_engine_hp)
      specs.push({ icon: Settings, label: lang === "ar" ? "القدرة" : "Power", value: `${v.truck_engine_hp} HP` });
    // iter327 — dynamic heavy-equipment specs (Phase 2). Admin-defined
    // fields stored under `heavy_specs`. Rendered as plain labelled values
    // so the detail page keeps working for old ads without this dict.
    if (v.heavy_specs && typeof v.heavy_specs === "object") {
      Object.entries(v.heavy_specs).forEach(([k, val]) => {
        if (val === "" || val === null || val === undefined) return;
        const display =
          typeof val === "boolean"
            ? (lang === "ar" ? (val ? "نعم" : "لا") : (val ? "Yes" : "No"))
            : String(val);
        // Humanise key: "boom_length_m" -> "Boom Length M"
        const humanised = k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        specs.push({ icon: Settings, label: humanised, value: display });
      });
    }
  }

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8" data-testid="vehicle-detail-page">
      {/* SEO Phase 1 — dynamic per-vehicle title + description + canonical.
          Uses only publicly available data (make/model/year/city). We DO NOT
          surface price here because listings may be "Receive Offers" with
          hidden pricing; keeping price out of metadata preserves that
          privacy contract. */}
      <Seo
        title={(() => {
          const parts = [v.year, v.make, v.model].filter(Boolean).join(" ");
          const cityPart = v.city ? (lang === "ar" ? ` في ${tr(v.city)}` : ` in ${tr(v.city)}`) : "";
          if (!parts) return undefined;
          return lang === "ar"
            ? `${parts} للبيع${cityPart} | كارتال`
            : `${parts} for Sale${cityPart} | CARTAL`;
        })()}
        description={(() => {
          const yr = v.year || "";
          const mm = [v.make, v.model].filter(Boolean).join(" ");
          const cityPart = v.city ? (lang === "ar" ? ` — ${tr(v.city)}` : ` — ${tr(v.city)}`) : "";
          if (!mm) return undefined;
          return lang === "ar"
            ? `${yr} ${mm} للبيع${cityPart} على منصة كارتال — بيانات الإعلان، صور، مواصفات، وتواصل مباشر مع البائع.`
            : `${yr} ${mm} for sale${cityPart} on CARTAL — listing details, images, specifications, and direct contact with the seller.`;
        })()}
        canonicalPath={`/vehicle/${id}`}
        noindex={v.status === "sold" || v.status === "deleted"}
        ogImage={(() => {
          const imgs = Array.isArray(v.images) ? v.images.filter(Boolean) : [];
          return imgs[0] || v.image || null;
        })()}
        jsonLd={(() => {
          // iter332 — Vehicle schema (+ Offer only when price is PUBLIC).
          // Hidden-price listings ("Receive Offers") MUST NOT leak price
          // through JSON-LD. We also emit BreadcrumbList that mirrors the
          // visible UI breadcrumbs.
          const canonicalUrl = `https://cartal.sa/vehicle/${id}`;
          const img = Array.isArray(v.images) && v.images.length ? v.images[0] : (v.image || null);
          const vehicle = {
            "@context": "https://schema.org",
            "@type": "Vehicle",
            "url": canonicalUrl,
            "name": [v.year, v.make, v.model].filter(Boolean).join(" "),
            ...(v.make ? { "brand": { "@type": "Brand", "name": String(v.make) } } : {}),
            ...(v.model ? { "model": String(v.model) } : {}),
            ...(v.year ? { "vehicleModelDate": String(v.year) } : {}),
            ...(v.mileage_km ? { "mileageFromOdometer": { "@type": "QuantitativeValue", "value": Number(v.mileage_km), "unitCode": "KMT" } } : {}),
            ...(v.transmission ? { "vehicleTransmission": String(v.transmission) } : {}),
            ...(v.fuel ? { "fuelType": String(v.fuel) } : {}),
            ...(v.color ? { "color": String(v.color) } : {}),
            ...(v.body ? { "bodyType": String(v.body) } : {}),
            ...(v.vin ? { "vehicleIdentificationNumber": String(v.vin) } : {}),
            ...(v.city ? { "areaServed": tr(v.city) } : {}),
            ...(img ? { "image": img } : {}),
          };
          // Offer schema ONLY when price is publicly visible and > 0
          if (v.price_visibility !== "hidden" && v.price_sar && Number(v.price_sar) > 0) {
            vehicle.offers = {
              "@type": "Offer",
              "priceCurrency": "SAR",
              "price": Number(v.price_sar),
              "availability": v.status === "sold"
                ? "https://schema.org/SoldOut"
                : "https://schema.org/InStock",
              "url": canonicalUrl,
              ...(v.city ? { "areaServed": tr(v.city) } : {}),
            };
          }
          const breadcrumb = {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": lang === "ar" ? "الرئيسية" : "Home", "item": "https://cartal.sa/" },
              { "@type": "ListItem", "position": 2, "name": lang === "ar" ? "السوق" : "Marketplace", "item": "https://cartal.sa/marketplace" },
              { "@type": "ListItem", "position": 3, "name": vehicle.name || (lang === "ar" ? "مركبة" : "Vehicle"), "item": canonicalUrl },
            ],
          };
          return [vehicle, breadcrumb];
        })()}
      />
      <Link to="/marketplace" className="text-sm text-[#6b6b6b] hover:text-[#0a0a0a]">
        ← {lang === "ar" ? "العودة للسوق" : "Back to marketplace"}
      </Link>

      {/* Demo listing banner — shown at the very top so buyers immediately
          know this is a placeholder, not a real ad. Contact routes go to
          the official CARTAL WhatsApp (not a fictitious seller). */}
      {isDemo && (
        <div
          className="mt-4 rounded-2xl p-4 sm:p-5"
          style={{
            background: "linear-gradient(135deg,rgba(245,158,11,0.14),rgba(180,83,9,0.10))",
            border: "1.5px solid rgba(245,158,11,0.55)",
          }}
          data-testid="demo-listing-banner"
        >
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md"
              style={{ background: "linear-gradient(135deg,#f59e0b,#b45309)", color: "#fff" }}
            >
              <span className="text-lg font-black">i</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-[#7c2d12] text-base">
                {lang === "ar" ? "إعلان تجريبي — للعرض فقط" : "Demo listing — display only"}
              </div>
              <p className="text-sm text-[#5a2409] mt-1 leading-relaxed">
                {lang === "ar"
                  ? "هذا الإعلان لأغراض العرض والاختبار فقط، وسيختفي تلقائياً عند إضافة إعلانات حقيقية من الشركاء. لأي استفسار تواصل مع كارتال مباشرة عبر واتساب."
                  : "This ad is for display / testing only and will disappear automatically once real partner listings appear. For any questions, contact CARTAL directly on WhatsApp."}
              </p>
              <a
                href={`https://wa.me/${CARTAL_WA}?text=${encodeURIComponent(waText)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-3 rounded-full px-4 py-2 text-sm font-bold shadow-md hover:scale-[1.02] transition-transform"
                style={{ background: "linear-gradient(135deg,#25D366,#128C7E)", color: "#fff" }}
                data-testid="demo-whatsapp-cta"
              >
                {lang === "ar" ? "تواصل مع كارتال على واتساب" : "Contact CARTAL on WhatsApp"}
                <span dir="ltr" className="opacity-80 text-xs">{CARTAL_CONTACT.whatsappNumber}</span>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ── Ad number pill — permanent public identifier for this listing */}
      {v.ad_number && (
        <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="vehicle-ad-number-row">
          <span
            className="inline-flex items-center gap-2 rounded-full px-3 py-1 border-2 font-mono text-xs sm:text-sm font-bold"
            style={{
              borderColor: "#c9a55c",
              backgroundColor: "rgba(201,165,92,0.10)",
              color: "#5a4615",
            }}
            title={lang === "ar" ? "الرقم الإعلاني الرسمي لهذا الإعلان" : "Official ad number for this listing"}
            data-testid="vehicle-ad-number"
            dir="ltr"
          >
            <span
              className="text-[9px] uppercase tracking-[0.22em] font-bold font-sans"
              style={{ color: "#8a6f2e" }}
            >
              {lang === "ar" ? "رقم الإعلان" : "Ad #"}
            </span>
            <span>{v.ad_number}</span>
            <button
              type="button"
              onClick={() => {
                try {
                  navigator.clipboard.writeText(v.ad_number);
                } catch (_) { /* noop */ }
              }}
              className="ms-1 text-[10px] font-sans font-semibold underline hover:no-underline"
              style={{ color: "#8a6f2e" }}
              data-testid="vehicle-ad-number-copy"
              aria-label={lang === "ar" ? "نسخ الرقم الإعلاني" : "Copy ad number"}
            >
              {lang === "ar" ? "نسخ" : "Copy"}
            </button>
          </span>

          {/* Inline mini QR — tap/click to open printable version */}
          <button
            type="button"
            onClick={() => setQrOpen(true)}
            className="inline-flex items-center gap-2 rounded-full pe-3 ps-1 py-1 border-2 transition-colors hover:bg-[#0a0a0a] hover:text-[#c9a55c]"
            style={{ borderColor: "#0a0a0a", backgroundColor: "#ffffff", color: "#0a0a0a" }}
            title={lang === "ar" ? "افتح رمز QR للطباعة أو المشاركة" : "Open QR code (print / share)"}
            data-testid="vehicle-qr-open"
            aria-label={lang === "ar" ? "افتح رمز QR" : "Open QR"}
          >
            <span
              className="inline-flex items-center justify-center rounded-full"
              style={{ backgroundColor: "#ffffff", padding: 3, border: "1px solid #0a0a0a" }}
            >
              <QRCodeSVG
                value={(() => {
                  try {
                    const u = new URL(typeof window !== "undefined" ? window.location.href : "");
                    u.searchParams.set("utm_source", "qr");
                    u.searchParams.set("utm_medium", "qrcode");
                    u.searchParams.set("utm_campaign", "share_listing");
                    if (v.id) u.searchParams.set("utm_content", v.id);
                    return u.toString();
                  } catch { return typeof window !== "undefined" ? window.location.href : ""; }
                })()}
                size={22}
                level="M"
                includeMargin={false}
                bgColor="#ffffff"
                fgColor="#0a0a0a"
              />
            </span>
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold">
              {lang === "ar" ? "رمز QR" : "QR"}
            </span>
          </button>
        </div>
      )}

      {/* ── QR modal — printable / shareable ───────────────────────────── */}
      {qrOpen && v.ad_number && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(10,10,10,0.75)", backdropFilter: "blur(6px)" }}
          onClick={() => setQrOpen(false)}
          data-testid="vehicle-qr-modal"
        >
          <div
            className="relative bg-white rounded-3xl shadow-2xl max-w-sm w-full p-6 sm:p-8 print:shadow-none print:p-2"
            onClick={(e) => e.stopPropagation()}
            id="cartal-qr-printable"
          >
            <button
              type="button"
              onClick={() => setQrOpen(false)}
              className="absolute top-3 end-3 w-8 h-8 rounded-full bg-[#f5f0e6] hover:bg-[#ede2c9] flex items-center justify-center transition-colors print:hidden"
              aria-label={lang === "ar" ? "إغلاق" : "Close"}
              data-testid="vehicle-qr-close"
            >
              <CloseIcon className="w-4 h-4 text-[#0a0a0a]" />
            </button>

            <div className="text-center">
              <div
                className="text-[10px] uppercase tracking-[0.28em] font-bold"
                style={{ color: "#8a6f2e" }}
              >
                CARTAL · {lang === "ar" ? "الرقم الإعلاني" : "Ad Number"}
              </div>
              <div
                className="font-mono text-2xl sm:text-3xl font-extrabold mt-1 mb-4"
                style={{ color: "#0a0a0a" }}
                dir="ltr"
              >
                {v.ad_number}
              </div>

              <div className="inline-block p-4 rounded-2xl border-4" style={{ borderColor: "#c9a55c" }}>
                <QRCodeSVG
                  value={(() => {
                    try {
                      const u = new URL(typeof window !== "undefined" ? window.location.href : "");
                      u.searchParams.set("utm_source", "qr");
                      u.searchParams.set("utm_medium", "qrcode");
                      u.searchParams.set("utm_campaign", "share_listing");
                      if (v.id) u.searchParams.set("utm_content", v.id);
                      return u.toString();
                    } catch { return typeof window !== "undefined" ? window.location.href : ""; }
                  })()}
                  size={220}
                  level="H"
                  includeMargin={false}
                  bgColor="#ffffff"
                  fgColor="#0a0a0a"
                  imageSettings={{
                    src: "/logo192.png",
                    height: 40,
                    width: 40,
                    excavate: true,
                  }}
                />
              </div>

              <p className="mt-4 text-sm text-[#3a3a3a] leading-relaxed">
                {lang === "ar"
                  ? "امسح الرمز بكاميرا هاتفك لفتح صفحة هذا الإعلان مباشرة. مثالي للطباعة على لوحة المعرض أو مشاركته عبر واتساب."
                  : "Scan with your phone camera to open this listing. Perfect for showroom signage or sharing on WhatsApp."}
              </p>

              <div className="mt-5 flex flex-wrap gap-2 justify-center print:hidden">
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold bg-[#0a0a0a] text-[#c9a55c] hover:bg-[#1a1a1a] transition-colors"
                  data-testid="vehicle-qr-print"
                >
                  {lang === "ar" ? "طباعة" : "Print"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    try {
                      const svg = document.querySelector("#cartal-qr-printable svg");
                      if (!svg) return;
                      const serializer = new XMLSerializer();
                      const source = serializer.serializeToString(svg);
                      const blob = new Blob([source], { type: "image/svg+xml;charset=utf-8" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `cartal-${v.ad_number}.svg`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      setTimeout(() => URL.revokeObjectURL(url), 1000);
                    } catch (_) { /* noop */ }
                  }}
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold border-2 border-[#0a0a0a] text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-[#c9a55c] transition-colors"
                  data-testid="vehicle-qr-download"
                >
                  {lang === "ar" ? "تحميل SVG" : "Download SVG"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    try {
                      navigator.clipboard.writeText(window.location.href);
                    } catch (_) { /* noop */ }
                  }}
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold border-2 transition-colors"
                  style={{ borderColor: "#c9a55c", color: "#8a6f2e" }}
                  data-testid="vehicle-qr-copy-link"
                >
                  {lang === "ar" ? "نسخ الرابط" : "Copy link"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Owner actions — only shown when the viewer owns this listing */}
      {isSelf && (
        <div
          className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border-2 p-4"
          style={{ borderColor: "#c9a55c", backgroundColor: "rgba(201,165,92,0.08)" }}
          data-testid="owner-actions-bar"
        >
          <div className="text-sm text-[#5a4615] inline-flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#8a6f2e]" />
            {lang === "ar"
              ? "هذا إعلانك — يمكنك تعديل البيانات أو حذف الإعلان أو تمييزه ليظهر في الأعلى."
              : "This is your listing — edit, delete, or boost it to the top."}
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to={`/vehicle/${id}/boost`}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold transition-colors shadow-sm"
              style={{ background: "linear-gradient(135deg,#c9a55c,#8a6f2e)", color: "#0a0a0a" }}
              data-testid="owner-boost-btn"
            >
              <Sparkles className="w-4 h-4" />
              {lang === "ar"
                ? (v.boost_active ? "تمديد التمييز" : "ميّز إعلانك")
                : (v.boost_active ? "Extend boost" : "Boost listing")}
            </Link>
            <Link
              to={`/vehicle/${id}/edit`}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold bg-[#0a0a0a] text-[#c9a55c] hover:bg-[#1a1a1a] transition-colors"
              data-testid="owner-edit-btn"
            >
              <Pencil className="w-4 h-4" />
              {lang === "ar" ? "تعديل الإعلان" : "Edit listing"}
            </Link>
          </div>
        </div>
      )}

      {/* ── Post-publish success + Salama prompt ────────────────────────
          Shown ONLY when the user has just published this listing AND the
          car does not have a valid periodic technical inspection.  Helps the
          seller close the loop on CARTAL without leaving for sites like
          ehkam.com / mvpi.gov.sa.

          iter315 — Marine (type === "boat") assets have NO Saudi periodic
          inspection, no ownership transfer via traffic dept., no Salama.
          For boats we render a neutral success banner with a "View marine
          section" CTA instead of the car-specific Salama funnel. */}
      {location.state?.justPublished && !justPublishedDismissed && v.type === "boat" && (
        <div
          className="mt-4 rounded-3xl overflow-hidden border-2 relative"
          style={{
            background: "linear-gradient(135deg, #0a0a0a 0%, #0f1a24 100%)",
            borderColor: "#1f6f8a",
          }}
          data-testid="post-publish-banner-marine"
        >
          <button
            type="button"
            onClick={() => setJustPublishedDismissed(true)}
            className="absolute top-3 end-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            aria-label="Dismiss"
            data-testid="post-publish-dismiss-marine"
            style={{ color: "#ffffff" }}
          >
            <CloseIcon className="w-4 h-4" />
          </button>

          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-3">
              <span
                className="inline-flex items-center justify-center w-10 h-10 rounded-full"
                style={{ backgroundColor: "rgba(46,160,67,0.18)", border: "1px solid #1f6f3a" }}
              >
                <CheckCircle2 className="w-5 h-5" style={{ color: "#4ade80" }} />
              </span>
              <div>
                <div
                  className="text-[10px] uppercase tracking-[0.28em] font-bold"
                  style={{ color: "#4ade80" }}
                >
                  {lang === "ar" ? "تم نشر الإعلان بنجاح" : "Listing published"}
                </div>
                <h2
                  className="font-display text-xl sm:text-2xl leading-tight font-semibold"
                  style={{ color: "#ffffff" }}
                >
                  {lang === "ar" ? "إعلانك البحري جاهز 🚤" : "Your marine listing is ready 🚤"}
                </h2>
              </div>
            </div>
            <p
              className="text-sm sm:text-base leading-relaxed max-w-2xl"
              style={{ color: "#e0e6ee" }}
            >
              {lang === "ar"
                ? "إعلانك متاح الآن للمهتمين بالقوارب واليخوت والأنشطة البحرية على كارتال. شارك الرابط للوصول إلى مشترين ومستأجرين جادّين."
                : "Your listing is now live for marine enthusiasts on CARTAL. Share the link to reach serious buyers and renters."}
            </p>
            <div className="flex flex-wrap gap-3 mt-5">
              <Link
                to="/marine"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-colors"
                style={{ backgroundColor: "#1f6f8a", color: "#ffffff" }}
                data-testid="post-publish-marine-cta"
              >
                <Anchor className="w-4 h-4" style={{ color: "#ffffff" }} />
                {lang === "ar" ? "الذهاب إلى القسم البحري" : "Go to the marine section"}
                <ArrowRight className="w-4 h-4 rtl:rotate-180" style={{ color: "#ffffff" }} />
              </Link>
              <button
                type="button"
                onClick={() => setJustPublishedDismissed(true)}
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold border-2 transition-colors"
                style={{
                  borderColor: "rgba(255,255,255,0.35)",
                  color: "#ffffff",
                  backgroundColor: "transparent",
                }}
                data-testid="post-publish-marine-close"
              >
                {lang === "ar" ? "إغلاق" : "Close"}
              </button>
            </div>
          </div>
        </div>
      )}

      {location.state?.justPublished && !justPublishedDismissed && v.type !== "boat" && (
        <div
          className="mt-4 rounded-3xl overflow-hidden border-2 relative"
          style={{
            background: "linear-gradient(135deg, #0a0a0a 0%, #1a1f12 100%)",
            borderColor: "#1f6f3a",
          }}
          data-testid="post-publish-banner"
        >
          <button
            type="button"
            onClick={() => setJustPublishedDismissed(true)}
            className="absolute top-3 end-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            aria-label="Dismiss"
            data-testid="post-publish-dismiss"
            style={{ color: "#ffffff" }}
          >
            <CloseIcon className="w-4 h-4" />
          </button>

          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-3">
              <span
                className="inline-flex items-center justify-center w-10 h-10 rounded-full"
                style={{ backgroundColor: "rgba(46,160,67,0.18)", border: "1px solid #1f6f3a" }}
              >
                <CheckCircle2 className="w-5 h-5" style={{ color: "#4ade80" }} />
              </span>
              <div>
                <div
                  className="text-[10px] uppercase tracking-[0.28em] font-bold"
                  style={{ color: "#4ade80" }}
                >
                  {lang === "ar" ? "تم نشر الإعلان بنجاح" : "Listing published"}
                </div>
                <h2
                  className="font-display text-xl sm:text-2xl leading-tight font-semibold"
                  style={{ color: "#ffffff" }}
                >
                  {v.periodic_inspection_valid
                    ? lang === "ar"
                      ? "إعلانك جاهز للبيع 🚀"
                      : "Your listing is ready 🚀"
                    : lang === "ar"
                      ? "خطوة واحدة لتكتمل صفقة سهلة: فحص سلامة"
                      : "One step left for a smooth deal: Salama inspection"}
                </h2>
              </div>
            </div>

            {!v.periodic_inspection_valid ? (
              <>
                <p
                  className="text-sm sm:text-base leading-relaxed mb-5 max-w-2xl"
                  style={{ color: "#e8e1cf" }}
                >
                  {lang === "ar"
                    ? "نظام المرور السعودي يشترط فحص فني دوري ساري قبل نقل الملكية. تصفّح مراكز الفحص المعتمدة (سلامة، فحص، المروج) من دليل كارتال وتواصل مباشرة مع المركز المناسب."
                    : "Saudi traffic law requires a valid periodic inspection before ownership transfer. Browse accredited inspection centers (Salama, Fahas, Al-Morooj) from CARTAL's directory and contact them directly."}
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/periodic-inspection"
                    className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-colors"
                    style={{ backgroundColor: "#1f6f3a", color: "#ffffff" }}
                    data-testid="post-publish-cta-book"
                  >
                    <ShieldCheck className="w-4 h-4" style={{ color: "#ffffff" }} />
                    {lang === "ar" ? "تصفّح مراكز الفحص الدوري" : "Browse periodic inspection centers"}
                    <ArrowRight className="w-4 h-4 rtl:rotate-180" style={{ color: "#ffffff" }} />
                  </Link>
                  <button
                    type="button"
                    onClick={() => setJustPublishedDismissed(true)}
                    className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold border-2 transition-colors"
                    style={{
                      borderColor: "rgba(255,255,255,0.35)",
                      color: "#ffffff",
                      backgroundColor: "transparent",
                    }}
                    data-testid="post-publish-skip"
                  >
                    {lang === "ar" ? "لاحقاً" : "Later"}
                  </button>
                </div>
              </>
            ) : (
              <p
                className="text-sm sm:text-base leading-relaxed max-w-2xl"
                style={{ color: "#e8e1cf" }}
              >
                {lang === "ar"
                  ? "إعلانك مكتمل ويظهر بشارة \"فحص دوري ساري\" مما يميّزك أمام المشترين الجادّين. شارك الرابط الآن."
                  : "Your listing is fully complete and carries the \"Periodic inspection valid\" badge, giving you an edge with serious buyers. Share your link now."}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8 mt-4">
        <div>
          {/* ── Image gallery ─────────────────────────────────────────────
              iter222 — the whole gallery is now width-bounded:
                • main image lives in a fixed-aspect frame (no reflow),
                • thumbnails scroll INSIDE their own strip (no page-level
                  horizontal overflow no matter the photo count),
                • at most 4 thumbnails are visible at once on mobile,
                • the counter "N / M" sits BELOW the frame (outside the
                  image) so it never covers content,
                • clicking the main image opens a fullscreen lightbox.
          */}
          <div
            className="rounded-3xl overflow-hidden border border-[#0a0a0a]/10 relative"
            data-testid="vehicle-gallery"
          >
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              className="block w-full text-left cursor-zoom-in"
              aria-label={lang === "ar" ? "افتح المعرض بملء الشاشة" : "Open fullscreen gallery"}
              data-testid="vehicle-main-image-btn"
            >
              <img
                src={images[activeIdx]}
                alt={(() => {
                  // iter332 — richer alt text for image SEO. Keeps strictly
                  // public info (no owner, no private data).
                  const base = [v.year, v.make, v.model].filter(Boolean).join(" ");
                  const city = v.city ? ` — ${tr(v.city)}` : "";
                  const idx = ` — ${lang === "ar" ? "صورة" : "image"} ${activeIdx + 1}/${images.length}`;
                  return (base || "CARTAL") + city + idx;
                })()}
                className="block w-full h-auto"
                data-testid="vehicle-main-image"
              />
            </button>

            {/* Navigation arrows (only if more than 1 image) */}
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={prevImg}
                  className="absolute top-1/2 -translate-y-1/2 start-3 w-10 h-10 rounded-full bg-black/55 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
                  aria-label="Previous image"
                  data-testid="gallery-prev"
                  style={{ color: "#ffffff" }}
                >
                  <ChevronLeft className="w-5 h-5 rtl:hidden" />
                  <ChevronRight className="w-5 h-5 ltr:hidden" />
                </button>
                <button
                  type="button"
                  onClick={nextImg}
                  className="absolute top-1/2 -translate-y-1/2 end-3 w-10 h-10 rounded-full bg-black/55 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
                  aria-label="Next image"
                  data-testid="gallery-next"
                  style={{ color: "#ffffff" }}
                >
                  <ChevronRight className="w-5 h-5 rtl:hidden" />
                  <ChevronLeft className="w-5 h-5 ltr:hidden" />
                </button>
              </>
            )}

            {(v.featured || v.boost_active) && (
              <span className="absolute top-4 start-4 pill pill-accent" data-testid="badge-featured">
                {lang === "ar" ? "مميز" : "Featured"}
              </span>
            )}
            <span
              className="absolute top-4 end-4 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest"
              style={{ backgroundColor: "rgba(0,0,0,0.6)", color: "#ffffff" }}
            >
              {tr(v.type)}
            </span>
          </div>

          {/* Photo counter — outside the image so it never covers content */}
          {images.length > 1 && (
            <div
              className="mt-2 text-xs font-semibold text-[#525252] text-center"
              data-testid="gallery-counter"
              dir="ltr"
            >
              {activeIdx + 1} / {images.length}
            </div>
          )}

          {/* Thumbnail carousel — HARD-CONTAINED viewport (overflow-hidden)
              with a JS-driven track. Exactly 3 thumbs visible on mobile;
              extras are visually clipped and reachable only via the
              left/right arrow buttons that shift the track by one page
              (3 thumbs) at a time. The viewport itself is `min-w-0
              overflow-hidden`, so no photo count can ever push the
              document horizontally. */}
          {images.length > 1 && (
            <div className="mt-2 min-w-0 max-w-full relative" data-testid="gallery-thumbs">
              <div
                ref={thumbsRef}
                className="overflow-hidden"
                style={{
                  scrollBehavior: "smooth",
                  scrollSnapType: "x mandatory",
                }}
              >
                <div className="flex gap-2">
                  {images.map((url, i) => (
                    <button
                      key={`${url}-${i}`}
                      type="button"
                      onClick={() => setActiveIdx(i)}
                      className={`relative h-16 sm:h-20 rounded-lg overflow-hidden border-2 transition-all ${
                        i === activeIdx
                          ? "border-[#c9a55c] ring-2 ring-[#c9a55c]/40"
                          : "border-transparent opacity-70 hover:opacity-100"
                      }`}
                      style={{
                        // 3 thumbs visible + 2 × 8px gaps = 100% viewport.
                        // flex:0 0 forces the thumb to keep this width so
                        // extras overflow the flex track and are clipped
                        // by the parent overflow-hidden viewport.
                        flex: "0 0 calc((100% - 1rem) / 3)",
                        scrollSnapAlign: "start",
                      }}
                      data-testid={`gallery-thumb-${i}`}
                      aria-label={`Image ${i + 1}`}
                    >
                      <img src={url} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
              {/* Paging arrows — only render when there are extra thumbs */}
              {images.length > THUMBS_PER_PAGE && (
                <>
                  <button
                    type="button"
                    onClick={() => scrollThumbs(-1)}
                    className="absolute top-1/2 -translate-y-1/2 start-1 w-8 h-8 rounded-full bg-black/60 hover:bg-black/85 text-white flex items-center justify-center"
                    aria-label={lang === "ar" ? "الصور السابقة" : "Previous thumbnails"}
                    data-testid="gallery-thumbs-prev"
                  >
                    <ChevronLeft className="w-4 h-4 rtl:hidden" />
                    <ChevronRight className="w-4 h-4 ltr:hidden" />
                  </button>
                  <button
                    type="button"
                    onClick={() => scrollThumbs(1)}
                    className="absolute top-1/2 -translate-y-1/2 end-1 w-8 h-8 rounded-full bg-black/60 hover:bg-black/85 text-white flex items-center justify-center"
                    aria-label={lang === "ar" ? "الصور التالية" : "Next thumbnails"}
                    data-testid="gallery-thumbs-next"
                  >
                    <ChevronRight className="w-4 h-4 rtl:hidden" />
                    <ChevronLeft className="w-4 h-4 ltr:hidden" />
                  </button>
                </>
              )}
            </div>
          )}

          {/* Fullscreen lightbox — mounted only when open */}
          {lightboxOpen && (
            <GalleryLightbox
              images={images}
              activeIdx={activeIdx}
              onIndexChange={setActiveIdx}
              onClose={() => setLightboxOpen(false)}
              lang={lang}
            />
          )}

          <div className="mt-6 surface rounded-2xl p-6">
            <div className="overline mb-2">{lang === "ar" ? "المواصفات" : "SPECIFICATIONS"}</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-2">
              {specs.map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.label}>
                    <div className="text-xs text-[#6b6b6b] inline-flex items-center gap-1">
                      <Icon className="w-3.5 h-3.5 text-[#8a6f2e]" />
                      {s.label}
                    </div>
                    <div className="font-display text-lg mt-1 text-[#0a0a0a]">{s.value}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-6 surface rounded-2xl p-6">
            <div className="overline mb-3">{lang === "ar" ? "الوصف" : "DESCRIPTION"}</div>
            <DescriptionBlock text={v.description} lang={lang} />
            <div className="text-xs text-[#8a8a8a] mt-4">{lang === "ar" ? "رقم الهيكل (VIN)" : "VIN"}: {v.vin}</div>
          </div>
          <EquipmentSection specs={v.specs} lang={lang} />

          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {/* iter315 — the whole car-services grid (Saudi traffic
                fines / registration / periodic inspection / financing /
                Mojaz / ownership transfer / pre-purchase check-up / car
                insurance) is car-specific by law and context. Marine
                assets (type === "boat") get NO fake service tiles here;
                we simply hide the block. Vehicle behaviour is unchanged. */}
            {v.type !== "boat" && (
              <>
                <ActionCard icon={Receipt} title={lang === "ar" ? "تسديد المخالفات المرورية" : "Pay traffic violations"} to={GOV_LINKS.trafficFines} external />
                <ActionCard icon={FileText} title={lang === "ar" ? "تجديد رخصة السير (الاستمارة)" : "Renew vehicle registration"} to={GOV_LINKS.vehicleRegistration} external />
                <ActionCard icon={ShieldCheck} title={lang === "ar" ? "احصل على تأمين" : "Get insurance"} to="/insurance" />
                <ActionCard icon={Stethoscope} title={lang === "ar" ? "فحص قبل الشراء" : "Pre-purchase check-up"} to="/pre-purchase-inspection" />
                <ActionCard icon={Wrench} title={lang === "ar" ? "فحص دوري" : "Periodic inspection"} to="/periodic-inspection" />
                <ActionCard icon={CreditCard} title={lang === "ar" ? "موّل هذه السيارة" : "Finance this car"} to="/financing" />
                <ActionCard icon={FileSearch} title={lang === "ar" ? "تقرير موجز للسيارة" : "Mojaz vehicle history"} to="/vehicle-history" />
                <ActionCard icon={ArrowRight} title={lang === "ar" ? "نقل الملكية" : "Ownership transfer"} to="/ownership-transfer" />
              </>
            )}
          </div>

          {/* Test-stage financing/installment plans — renders only for
              eligible listings (verified dealer/official_agency).
              Individual/private sellers get nothing.
              iter315 — hidden entirely for marine listings (car financing
              product doesn't apply to boats). */}
          {v.type !== "boat" && <VehicleFinancingPlans vehicleId={id} />}

          {/* PRIMARY CTA — Complete the deal (placed RIGHT BELOW the 6 services so the
              user sees it as "the next step" but is NOT required to use any service first.)
              iter156 — Hidden from the UI via FEATURES.COMPLETE_DEAL_CTA_ENABLED. The
              route, DealCompletionPage, and every backend endpoint stay intact so
              flipping the flag back to `true` restores the button exactly as it was. */}
          {!isSelf && FEATURES.COMPLETE_DEAL_CTA_ENABLED && (
            <div className="mt-5">
              <Link
                to={`/deal/${id}`}
                className="group flex items-center justify-center gap-3 rounded-2xl px-6 py-4 w-full text-base font-bold transition-all shadow-md hover:shadow-xl"
                style={{ background: "linear-gradient(135deg,#c9a55c 0%,#8a6f2e 100%)", color: "#0a0a0a" }}
                data-testid="complete-deal-cta-main"
              >
                <CheckCircle2 className="w-5 h-5" />
                <span>{lang === "ar" ? "إتمام الصفقة عبر كارتال" : "Complete the deal via CARTAL"}</span>
                <ArrowRight className="w-4 h-4 rtl:rotate-180 transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" />
              </Link>
              <p className="text-[11px] text-[#6b6b6b] text-center mt-2" data-testid="complete-deal-hint">
                {lang === "ar"
                  ? "الخدمات أعلاه اختيارية — يمكنك إتمام الصفقة مباشرة بدفع رسوم المنصة فقط."
                  : "The services above are optional — you can complete the deal directly by paying the platform fee only."}
              </p>
            </div>
          )}
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start space-y-4">
          {FEATURES.AUCTIONS_ENABLED && v.listing_type === "auction" && (
            <AuctionBidBox
              vehicle={v}
              onBidPlaced={(res) => {
                // Refresh vehicle data after a successful bid so the
                // current_bid_sar / bid_count / status update inline.
                apiClient.get(`/vehicles/${v.id}`).then(({ data }) => setV(data)).catch(() => {});
              }}
            />
          )}
          <div className="surface rounded-2xl p-6" data-testid="vehicle-sidebar">
            {/* iter221 — sold banner. Placed above the model heading
                so a viewer sees the status the moment the sidebar
                loads, but INSIDE the surface card so it never
                overlaps the image gallery, the corner chips, or the
                sticky contact bar. Only renders when the owner has
                flipped the flag on. */}
            {v.is_sold && (
              <div className="mb-3 flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-bold"
                   style={{ background: "rgba(15,124,58,0.10)", color: "#0f7c3a", border: "1.5px solid rgba(15,124,58,0.35)" }}
                   data-testid="detail-sold-banner">
                <CheckCircle2 className="w-4 h-4" />
                {lang === "ar" ? "هذه المركبة مباعة" : "This vehicle is sold"}
              </div>
            )}
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="overline mb-2">{v.year} · {v.make}</div>
                <h1 className="font-display text-3xl tracking-tight font-medium text-[#0a0a0a]">{v.model}</h1>
                {/* iter319 — "SERVICE PROVIDER PROFILE ↔ portal only" rule.
                    The old "مقدَّم من {business}" badge here linked every
                    ad detail page directly into `/company/{slug}` — the
                    unified Service Provider Profile — which the owner
                    now defines as usable ONLY inside the Service
                    Providers Portal (/services/*, /insurance, /financing,
                    /workshop, /car-wash, /parts, /marine partner cards,
                    /companies directory).
                    An ad detail page is an ADVERTISEMENT surface, not a
                    portal, so this link was a leak. Removed.
                    Listing attribution stays intact through the
                    "عرض جميع إعلانات هذا البائع" link below (points to
                    the neutral `/seller/{id}` page — seller's other
                    listings only, NOT the provider profile). */}
              </div>
              {!isSelf && (
                <button
                  type="button"
                  onClick={() => {
                    if (!user) {
                      navigate("/login", { state: { from: `/vehicle/${id}` } });
                      return;
                    }
                    setReportOpen(true);
                  }}
                  className="shrink-0 inline-flex items-center gap-1 rounded-full px-2.5 py-1.5 text-[11px] font-bold border border-red-200 bg-red-50 text-red-700 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
                  data-testid="report-listing-btn-sidebar"
                  title={lang === "ar" ? "بلاغ تحت الإعلان" : "Report this listing"}
                  aria-label={lang === "ar" ? "بلاغ تحت الإعلان" : "Report this listing"}
                >
                  <Flag className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{lang === "ar" ? "بلاغ" : "Report"}</span>
                </button>
              )}
            </div>
            <div className="flex items-baseline gap-2 mt-4">
              {FEATURES.AUCTIONS_ENABLED && v.listing_type === "auction" ? (
                <div className="text-xs text-[#8a8a8a] uppercase tracking-widest font-bold">
                  {lang === "ar" ? "المزايدة تبدأ من" : "Bidding opens at"} <span className="text-[#8a6f2e] font-display text-base ms-1">{formatSAR(v.starting_bid_sar || v.price_sar)} SAR</span>
                </div>
              ) : v.price_visibility === "hidden" ? (
                <div className="flex flex-col gap-1" data-testid="price-hidden-block">
                  <div className="inline-flex items-center gap-2 text-[#8a6f2e] font-display text-2xl sm:text-3xl font-bold">
                    <EyeOff className="w-5 h-5 opacity-70" />
                    {lang === "ar" ? "السعر غير معلن" : "Price on request"}
                  </div>
                  <div className="text-[12px] text-[#525252]">
                    {lang === "ar"
                      ? "قدّم عرضك — يصل البائع مباشرة وبسرية."
                      : "Submit a private offer directly to the seller."}
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-4xl font-bold text-[#8a6f2e] font-display">{formatSAR(v.price_sar)}</div>
                  <div className="text-sm text-[#6b6b6b]">SAR{isRental ? t("common.perDay") : ""}</div>
                </>
              )}
            </div>
            {/* iter247 — inline sale-method chips (no section title).
                Renders only when the seller attached options. Sits
                right under the price so buyers see "how they can buy"
                at a glance without disrupting cash-price hierarchy. */}
            {Array.isArray(v.sale_options) && v.sale_options.length > 0 && (
              <div
                className="mt-2 flex flex-wrap gap-1.5"
                data-testid="detail-sale-options"
              >
                {v.sale_options.map((id) => {
                  const opt = SALE_OPTIONS_INDEX[id];
                  if (!opt) return null;
                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold"
                      style={{
                        background: "rgba(201,165,92,0.10)",
                        color: "#8a6f2e",
                        border: "1px solid rgba(201,165,92,0.35)",
                      }}
                      data-testid={`detail-sale-option-${id}`}
                    >
                      <span aria-hidden>{opt.glyph}</span>
                      <span>{lang === "ar" ? opt.ar : opt.en}</span>
                    </span>
                  );
                })}
              </div>
            )}
            <div className="mt-2 text-sm text-[#6b6b6b] inline-flex items-center gap-1">
              <MapPin className="w-4 h-4 text-[#8a6f2e]" /> {v.city}
            </div>

            {/* ── Periodic inspection badge ───────────────────────────────
                iter315 — periodic inspection is a car-only Saudi construct
                (Salama / Fahas centres, MVPI). Marine assets (type === "boat")
                do NOT undergo this and cannot be marked either way, so we
                hide the whole badge for them. */}
            {v.type !== "boat" && (v.periodic_inspection_valid ? (
              <div
                className="mt-4 flex items-start gap-2 rounded-xl p-3"
                style={{ backgroundColor: "rgba(46,160,67,0.08)", border: "1px solid rgba(46,160,67,0.35)" }}
                data-testid="badge-periodic-valid"
              >
                <ShieldCheck className="w-5 h-5 text-green-700 shrink-0 mt-0.5" />
                <div className="text-sm leading-tight">
                  <div className="font-semibold text-green-800">
                    {lang === "ar" ? "فحص دوري ساري" : "Periodic inspection valid"}
                  </div>
                  {v.periodic_inspection_expiry && (
                    <div className="text-xs text-green-700/80 mt-0.5">
                      {lang === "ar" ? "ساري حتى:" : "Expires:"} {v.periodic_inspection_expiry}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div
                className="mt-4 flex items-start gap-2 rounded-xl p-3"
                style={{ backgroundColor: "rgba(201,165,92,0.10)", border: "1px solid rgba(201,165,92,0.40)" }}
                data-testid="badge-periodic-missing"
              >
                <AlertTriangle className="w-5 h-5 text-[#8a6f2e] shrink-0 mt-0.5" />
                <div className="text-sm leading-tight">
                  <div className="font-semibold text-[#5a4615]">
                    {lang === "ar" ? "بدون فحص دوري" : "No periodic inspection"}
                  </div>
                  <Link
                    to="/periodic-inspection"
                    className="text-xs text-[#8a6f2e] hover:text-[#5a4615] underline mt-0.5 inline-block"
                    data-testid="link-book-periodic"
                  >
                    {lang === "ar" ? "احجز فحص سلامة/فحص قبل نقل الملكية" : "Book Salama/Fahas before transfer"}
                  </Link>
                </div>
              </div>
            ))}

            {/* ── Contact actions ───────────────────────────────────────── */}
            <div className="mt-6 flex flex-col gap-2" data-testid="contact-actions">
              {/* Primary: in-app chat */}
              <button
                type="button"
                className="btn-primary justify-center"
                onClick={contactSeller}
                disabled={contactBusy || isSelf}
                data-testid="contact-seller-btn"
              >
                {contactBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
                {isSelf
                  ? lang === "ar"
                    ? "هذا إعلانك"
                    : "Your listing"
                  : lang === "ar"
                    ? "راسل البائع"
                    : "Message seller"}
              </button>

              {/* Phone + WhatsApp row */}
              <div className="flex gap-2">
                {phoneE164 ? (
                  <a
                    href={`tel:${phoneE164}`}
                    className="btn-ghost justify-center flex-1"
                    data-testid="call-seller-btn"
                    aria-label="Call seller"
                    onClick={() => {
                      // iter248 — non-blocking analytics ping. Never awaited,
                      // never blocks the dialer from opening.
                      try {
                        apiClient.post("/analytics/contact-events", {
                          vehicle_id: v.id,
                          kind: "phone_click",
                        }).catch(() => {});
                      } catch { /* ignore */ }
                    }}
                  >
                    <Phone className="w-4 h-4" /> {lang === "ar" ? "اتصل" : "Call"}
                  </a>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="btn-ghost justify-center flex-1 opacity-50 cursor-not-allowed"
                    data-testid="call-seller-disabled"
                  >
                    <Phone className="w-4 h-4" /> {lang === "ar" ? "لا يوجد رقم" : "No phone"}
                  </button>
                )}
                {waDigits ? (
                  <a
                    href={`https://wa.me/${waDigits}?text=${encodeURIComponent(waText)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 flex-1 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors"
                    style={{ backgroundColor: "#25D366", color: "#ffffff", border: "1px solid #25D366" }}
                    data-testid="whatsapp-seller-btn"
                    aria-label="WhatsApp seller"
                    onClick={() => {
                      // iter248 — non-blocking analytics ping.
                      try {
                        apiClient.post("/analytics/contact-events", {
                          vehicle_id: v.id,
                          kind: "whatsapp_click",
                        }).catch(() => {});
                      } catch { /* ignore */ }
                    }}
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#ffffff" aria-hidden="true">
                      <path d="M20.52 3.48A11.86 11.86 0 0012.04 0C5.42 0 .06 5.36.06 11.98c0 2.11.55 4.16 1.6 5.98L0 24l6.21-1.63a11.95 11.95 0 005.83 1.49h.01c6.62 0 11.98-5.36 11.98-11.98 0-3.2-1.25-6.21-3.51-8.4zM12.05 21.8h-.01a9.84 9.84 0 01-5.01-1.37l-.36-.22-3.69.97.99-3.59-.23-.37a9.83 9.83 0 01-1.51-5.24c0-5.43 4.42-9.85 9.86-9.85 2.63 0 5.1 1.03 6.96 2.89a9.78 9.78 0 012.88 6.97c-.01 5.43-4.43 9.81-9.88 9.81zm5.41-7.37c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.03 1.01-1.03 2.46s1.06 2.86 1.21 3.06c.15.2 2.08 3.17 5.04 4.45.7.3 1.25.48 1.68.62.71.22 1.35.19 1.86.12.57-.08 1.76-.72 2-1.41.25-.69.25-1.28.18-1.41-.07-.13-.27-.2-.57-.35z"/>
                    </svg>
                    {lang === "ar" ? "واتساب" : "WhatsApp"}
                  </a>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="btn-ghost justify-center flex-1 opacity-50 cursor-not-allowed"
                  >
                    <MessageSquare className="w-4 h-4" /> {lang === "ar" ? "واتساب" : "WhatsApp"}
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={onFav}
                className={`btn-ghost justify-center ${fav ? "border-[#c9a55c] text-[#8a6f2e]" : ""}`}
                data-testid="favorite-btn"
                aria-label="Favorite"
              >
                <Heart className={`w-4 h-4 ${fav ? "fill-current" : ""}`} />
                {fav ? (lang === "ar" ? "محفوظة" : "Saved") : (lang === "ar" ? "احفظ في المفضلة" : "Save to favorites")}
              </button>

              {/* PRIMARY CTA moved next to the 6 services panel (see above).
                  Sidebar stays focused on direct contact actions only. */}

              {v.prefer_contact && (
                <p className="text-[11px] text-[#8a6f2e] text-center mt-1" data-testid="prefer-contact-hint">
                  {lang === "ar"
                    ? `يفضّل البائع التواصل عبر: ${
                        v.prefer_contact === "phone" ? "الاتصال" :
                        v.prefer_contact === "whatsapp" ? "واتساب" : "رسائل كارتال"
                      }`
                    : `Seller prefers: ${
                        v.prefer_contact === "phone" ? "Call" :
                        v.prefer_contact === "whatsapp" ? "WhatsApp" : "CARTAL chat"
                      }`}
                </p>
              )}

              {(v.seller_id || v.owner_id) && (
                <Link
                  to={`/seller/${v.seller_id || v.owner_id}`}
                  className="mt-2 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-[#8a6f2e] hover:text-[#c9a55c] transition-colors"
                  data-testid="view-seller-profile"
                >
                  {lang === "ar" ? "عرض جميع إعلانات هذا البائع →" : "View all listings by this seller →"}
                </Link>
              )}

              {/* Phase 2A — "قدّم عرضك" CTA (only when price is hidden and user is not the seller) */}
              {v.price_visibility === "hidden" && !isSelf && (
                <button
                  type="button"
                  onClick={() => setOfferOpen(true)}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full h-11 text-sm font-bold shadow-sm hover:scale-[1.01] transition-transform"
                  style={{
                    background: "linear-gradient(135deg,#e8c873,#c9a55c)",
                    color: "#0a0a0a",
                  }}
                  data-testid="btn-submit-offer"
                >
                  <HandCoins className="w-4 h-4" />
                  {lang === "ar" ? "قدّم عرضك" : "Submit an offer"}
                </button>
              )}
            </div>
          </div>

          {/* Phase 2A — Seller-only inline offers panel */}
          {isSelf && offersData && (
            <div className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-5" data-testid="seller-offers-panel">
              <div className="flex items-center gap-2 mb-3">
                <Inbox className="w-4 h-4 text-[#8a6f2e]" />
                <h3 className="font-display font-bold text-[#0a0a0a]">
                  {lang === "ar" ? `العروض المستلمة (${offersData.total})` : `Offers received (${offersData.total})`}
                </h3>
              </div>
              {offersData.total === 0 ? (
                <div className="text-sm text-[#8a8a8a]">
                  {lang === "ar"
                    ? (v.price_visibility === "hidden"
                        ? "لم يصل أي عرض بعد. حالة الإعلان: إخفاء السعر مُفعّل."
                        : "لم يصل أي عرض. لتفعيل استقبال العروض بشكل بارز، فعّل «إخفاء السعر» من تعديل الإعلان.")
                    : (v.price_visibility === "hidden"
                        ? "No offers yet. Price is hidden."
                        : "No offers yet. To make offers more visible, hide the price from Edit listing.")}
                </div>
              ) : (
                <div className="space-y-2">
                  {offersData.items.map((o) => {
                    const tone =
                      o.status === "pending" ? "bg-amber-50 text-amber-700 border-amber-200" :
                      o.status === "accepted" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                      o.status === "rejected" ? "bg-red-50 text-red-700 border-red-200" :
                      "bg-neutral-100 text-neutral-700 border-neutral-200";
                    const statusLbl =
                      o.status === "pending" ? (lang === "ar" ? "قيد المراجعة" : "Pending") :
                      o.status === "accepted" ? (lang === "ar" ? "مقبول" : "Accepted") :
                      o.status === "rejected" ? (lang === "ar" ? "مرفوض" : "Rejected") :
                      (lang === "ar" ? "مسحوب" : "Withdrawn");
                    return (
                      <div key={o.id} className="rounded-xl border border-[#0a0a0a]/[0.06] p-3 flex flex-wrap gap-3 items-start" data-testid={`offer-item-${o.id}`}>
                        <div className="flex-1 min-w-[200px]">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="text-lg font-display font-bold text-[#0a0a0a]">
                              {formatSAR(o.amount_sar)} <span className="text-xs text-[#8a8a8a] font-normal">SAR</span>
                            </div>
                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${tone}`}>
                              {statusLbl}
                            </span>
                          </div>
                          <div className="text-[12px] text-[#525252] mt-1">
                            <span className="font-semibold">{o.buyer_name || (lang === "ar" ? "مقدّم عرض" : "Offerer")}</span>
                            {o.buyer_phone && (<> · <a href={`tel:${o.buyer_phone}`} className="hover:text-[#0a0a0a]" dir="ltr">{o.buyer_phone}</a></>)}
                          </div>
                          {o.message && (
                            <div className="mt-1.5 text-[12px] text-[#525252] bg-[#faf9f5] rounded-lg px-3 py-1.5">
                              {o.message}
                            </div>
                          )}
                          <div className="text-[10px] text-[#8a8a8a] mt-1">
                            {new Date(o.created_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-US")}
                          </div>
                        </div>
                        {o.status === "pending" && (
                          <div className="flex gap-1.5">
                            <button
                              type="button"
                              onClick={() => decideOffer(o.id, "accept")}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-bold bg-emerald-600 text-white hover:bg-emerald-700"
                              data-testid={`offer-accept-${o.id}`}
                            >
                              <Check className="w-3.5 h-3.5" /> {lang === "ar" ? "قبول" : "Accept"}
                            </button>
                            <button
                              type="button"
                              onClick={() => decideOffer(o.id, "reject")}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-bold bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                              data-testid={`offer-reject-${o.id}`}
                            >
                              <Ban className="w-3.5 h-3.5" /> {lang === "ar" ? "رفض" : "Reject"}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="text-[10px] text-[#8a8a8a] mt-3">
                {lang === "ar"
                  ? "قبول العرض لا يُعتبر بيعًا تلقائيًا — يمكنك متابعة التواصل مع المشتري."
                  : "Accepting is a soft approval. You keep control and continue the chat with the buyer."}
              </div>
            </div>
          )}

          {!FEATURES.ESCROW_ENABLED ? (
            /* Escrow is regulator-gated (SAMA PI licence). Until we finalise
               the licensing path, show a clear, non-clickable "Coming Soon"
               tile so buyers know the feature is on the roadmap and
               understand why it isn't purchasable yet. */
            <div
              className="rounded-2xl p-6 relative overflow-hidden"
              style={{
                background: "linear-gradient(135deg,#fbf6e8 0%,#f5efe0 100%)",
                border: "2px dashed rgba(201,165,92,0.55)",
              }}
              data-testid="escrow-coming-soon-card"
            >
              <div className="flex items-center gap-2 text-[#8a6f2e]">
                <ShieldCheck className="w-5 h-5" />
                <div className="overline">
                  {lang === "ar" ? "ضمان كارتال — قريباً" : "CARTAL Guarantee — Coming Soon"}
                </div>
              </div>
              <p className="text-sm text-[#3a3a3a] mt-3 leading-relaxed">
                {lang === "ar"
                  ? "خدمة الاحتجاز المالي (ضمان كارتال) قيد الإعداد التنظيمي مع الجهات المرخّصة. سيتم إطلاقها قريباً كطبقة حماية إضافية للمشتري والبائع."
                  : "Our secure funds-holding service (CARTAL Guarantee) is being finalised with licensed regulatory partners. It will launch soon as an added protection layer for buyers and sellers."}
              </p>
              <span
                className="inline-flex items-center gap-1.5 mt-4 px-3 py-1 rounded-full text-[11px] font-bold"
                style={{
                  background: "linear-gradient(135deg,#f5d478,#c9a55c,#8a6f2e)",
                  color: "#0a0a0a",
                }}
              >
                <Sparkles className="w-3 h-3" />
                {lang === "ar" ? "قريباً" : "Coming Soon"}
              </span>
            </div>
          ) : v.escrow_enabled ? (
            <div
              className="rounded-2xl p-6 relative overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #0a0a0a 0%, #1a1f12 100%)",
                border: "2px solid #c9a55c",
              }}
              data-testid="escrow-active-card"
            >
              <div className="flex items-center gap-2" style={{ color: "#d4ae6a" }}>
                <ShieldCheck className="w-5 h-5" style={{ color: "#d4ae6a" }} />
                <div className="overline" style={{ color: "#d4ae6a" }}>
                  {lang === "ar" ? "محمي بضمان كارتال" : "Protected by CARTAL Escrow"}
                </div>
              </div>
              <p className="text-sm leading-relaxed mt-3" style={{ color: "#e8e1cf" }}>
                {lang === "ar"
                  ? "البائع وافق على استخدام ضمان كارتال على هذا الإعلان. يمكنك دفع ثمن السيارة عبر كارتال ويبقى المبلغ محجوزاً حتى تستلم السيارة وتؤكد رضاك."
                  : "The seller opted in to use CARTAL Escrow on this listing. You can pay through CARTAL and the funds remain held until you receive the car and confirm satisfaction."}
              </p>
              <ul className="text-xs space-y-2 mt-4" style={{ color: "#e8e1cf" }}>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "#d4ae6a" }} />
                  {lang === "ar" ? "الأموال محتجزة بأمان" : "Funds held securely"}
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "#d4ae6a" }} />
                  {lang === "ar" ? "تحرير بعد الاستلام" : "Released after receipt"}
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "#d4ae6a" }} />
                  {lang === "ar" ? "وساطة عند النزاع" : "Mediation on dispute"}
                </li>
              </ul>
              <p
                className="text-[10px] mt-4 leading-relaxed"
                style={{ color: "rgba(232,225,207,0.65)" }}
                data-testid="escrow-disclaimer"
              >
                {lang === "ar"
                  ? "تنويه: كارتال مزوّد تقني فقط ولا تتحمل أي مسؤولية قانونية عن السيارة أو الصفقة. الضمان آلية احتجاز مالي اختيارية. "
                  : "Notice: CARTAL is only a technical facilitator and bears no legal liability for the vehicle or the transaction. Escrow is an optional financial holding mechanism. "}
                <Link
                  to="/escrow-terms"
                  className="underline font-semibold"
                  style={{ color: "#d4ae6a" }}
                  data-testid="link-escrow-terms-detail"
                >
                  {lang === "ar" ? "الشروط" : "Terms"}
                </Link>
              </p>
            </div>
          ) : (
            <div className="surface rounded-2xl p-6" data-testid="escrow-invite-card">
              <div className="flex items-center gap-2 text-[#8a6f2e]">
                <ShieldCheck className="w-5 h-5" />
                <div className="overline">{lang === "ar" ? "ضمان كارتال (غير مفعّل)" : "CARTAL Escrow (not enabled)"}</div>
              </div>
              <p className="text-sm text-[#3a3a3a] mt-3 leading-relaxed">
                {lang === "ar"
                  ? "البائع لم يُفعّل ضمان كارتال على هذا الإعلان. تواصل معه مباشرة أو اطلب منه تفعيل الضمان قبل الدفع للحصول على حماية إضافية."
                  : "The seller has not enabled CARTAL Escrow on this listing. Contact them directly or ask them to enable Escrow before paying for added protection."}
              </p>
              <Link to="/trust" className="text-[#8a6f2e] text-sm mt-4 inline-flex items-center gap-1 font-semibold hover:text-[#5a4615]">
                {lang === "ar" ? "كيف يعمل الضمان" : "How escrow works"} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
              </Link>
            </div>
          )}

          <div className="surface rounded-2xl p-6">
            <div className="overline mb-2">{lang === "ar" ? "البائع" : "SELLER"}</div>
            <Link to={v.seller_id ? `/sellers/${v.seller_id}` : "#"} className="block hover:opacity-80 transition-opacity" data-testid="vehicle-seller-link">
              <div className="font-display text-lg text-[#0a0a0a] inline-flex items-center gap-2 flex-wrap">
                {v.seller_name}
                {(v.seller_verified || v.business?.verified) && (
                  <VerifiedBadge
                    status="approved"
                    type={v.seller_kyc_type || (v.business?.verified ? "business" : null)}
                    lang={lang}
                    size="md"
                  />
                )}
              </div>
              <div className="text-xs text-[#8a8a8a] mt-1">{tr(v.seller_type)}</div>
            </Link>
            <SellerTrustSummary sellerId={v.seller_id} lang={lang} />
            {!v.seller_verified && !v.business?.verified && (
              <div className="mt-3 text-xs text-[#8a6f2e] inline-flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" />
                {lang === "ar" ? "البائع لم يُكمل التحقق من هويته بعد" : "Seller has not completed identity verification"}
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* ── Share listing (أعجبني في كارتال) ──────────────────────────── */}
      <div className="mt-10 max-w-3xl mx-auto" data-testid="share-section">
        <ShareBar
          url={typeof window !== "undefined" ? `${window.location.origin}/vehicle/${v.id}?lang=${lang}` : undefined}
          title={`${v.year} ${v.make} ${v.model}`}
          price={v.price_sar}
          city={v.city}
          vehicleId={v.id}
        />
      </div>

      {/* ── Similar listings ────────────────────────────────────────────── */}
      {similar.length > 0 && (
        <section className="mt-14" data-testid="similar-section">
          <div className="flex items-end justify-between gap-4 mb-5">
            <div>
              <div className="overline mb-1">{lang === "ar" ? "عروض مشابهة" : "SIMILAR LISTINGS"}</div>
              <h2 className="font-display text-2xl sm:text-3xl tracking-tight text-[#0a0a0a]">
                {lang === "ar" ? `سيارات أخرى من ${v.make} في نطاق سعرك` : `More ${v.make} cars in your price range`}
              </h2>
            </div>
            <Link
              to={`/marketplace?make=${encodeURIComponent(v.make)}`}
              className="text-sm text-[#8a6f2e] hover:text-[#5a4615] font-semibold inline-flex items-center gap-1"
              data-testid="similar-see-all"
            >
              {lang === "ar" ? "عرض الكل" : "See all"}
              <ArrowRight className="w-4 h-4 rtl:rotate-180" />
            </Link>
          </div>
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [&>*]:break-inside-avoid [&>*]:mb-5" data-testid="similar-grid">
            {similar.slice(0, 6).map((s) => (
              <VehicleCard key={s.id} v={s} />
            ))}
          </div>
        </section>
      )}

      {/* ── Report listing CTA (always visible to non-owners) ───────── */}
      {!isSelf && (
        <div className="mt-12 flex justify-center" data-testid="report-section">
          <button
            type="button"
            onClick={() => {
              if (!user) {
                navigate("/login", { state: { from: `/vehicle/${id}` } });
                return;
              }
              setReportOpen(true);
            }}
            className="inline-flex items-center gap-2.5 rounded-full px-6 py-3 text-sm font-bold border-2 border-red-300 bg-red-50 text-red-700 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors shadow-sm"
            data-testid="report-listing-btn"
            aria-label={lang === "ar" ? "بلاغ" : "Report"}
          >
            <Flag className="w-4 h-4" />
            {lang === "ar" ? "بلاغ" : "Report"}
          </button>
        </div>
      )}

      {/* ── Report modal ────────────────────────────────────────────── */}
      {reportOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          data-testid="report-modal"
        >
          <div className="bg-white rounded-3xl max-w-md w-full p-7 shadow-2xl">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-red-100">
                  <Flag className="w-5 h-5 text-red-600" />
                </span>
                <h3 className="font-display text-xl font-semibold">
                  {lang === "ar" ? "بلاغ عن الإعلان" : "Report this listing"}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => !reportBusy && setReportOpen(false)}
                className="w-8 h-8 rounded-full hover:bg-[#0a0a0a]/5 flex items-center justify-center"
                aria-label="Close"
                data-testid="report-close"
              >
                <CloseIcon className="w-4 h-4" />
              </button>
            </div>
            {reportDone ? (
              <div className="py-6 text-center" data-testid="report-success">
                <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
                <p className="font-semibold text-[#0a0a0a]">
                  {lang === "ar" ? "تم استلام البلاغ — شكراً لك" : "Report received — thank you"}
                </p>
                <p className="text-xs text-[#6b6b6b] mt-1">
                  {lang === "ar" ? "سيراجع فريق كارتال البلاغ خلال 24 ساعة." : "The CARTAL team will review within 24 hours."}
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm text-[#525252] leading-relaxed mb-4">
                  {lang === "ar"
                    ? "ساعدنا في إبقاء كارتال آمنة. اختر السبب واكتب وصفاً موجزاً."
                    : "Help keep CARTAL safe. Pick a reason and describe the issue briefly."}
                </p>
                <div className="space-y-2 mb-4">
                  {[
                    { id: "misleading", ar: "معلومات مضللة أو سعر مزيّف", en: "Misleading info / fake price" },
                    { id: "scam", ar: "اشتباه احتيال", en: "Suspected scam" },
                    { id: "duplicate", ar: "إعلان مكرر", en: "Duplicate listing" },
                    { id: "sold", ar: "السيارة مباعة بالفعل", en: "Already sold" },
                    { id: "inappropriate", ar: "محتوى غير لائق", en: "Inappropriate content" },
                    { id: "other", ar: "سبب آخر", en: "Other" },
                  ].map((opt) => (
                    <label
                      key={opt.id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
                        reportReason === opt.id
                          ? "border-[#c9a55c] bg-[#fdf9ef]"
                          : "border-[#0a0a0a]/10 hover:border-[#c9a55c]/50"
                      }`}
                      data-testid={`report-reason-${opt.id}`}
                    >
                      <input
                        type="radio"
                        name="report-reason"
                        value={opt.id}
                        checked={reportReason === opt.id}
                        onChange={(e) => setReportReason(e.target.value)}
                        className="accent-[#c9a55c]"
                      />
                      <span>{lang === "ar" ? opt.ar : opt.en}</span>
                    </label>
                  ))}
                </div>
                <textarea
                  className="input-dark"
                  rows={3}
                  placeholder={lang === "ar" ? "صف المشكلة بإيجاز…" : "Describe the issue briefly…"}
                  value={reportNotes}
                  onChange={(e) => setReportNotes(e.target.value)}
                  data-testid="report-notes"
                />
                <button
                  type="button"
                  onClick={submitReport}
                  disabled={reportBusy}
                  className="btn-primary w-full justify-center mt-4"
                  data-testid="report-submit"
                >
                  {reportBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flag className="w-4 h-4" />}
                  {lang === "ar" ? "إرسال البلاغ" : "Submit report"}
                </button>
                <p className="text-[10px] text-[#8a8a8a] mt-3 text-center leading-relaxed">
                  {lang === "ar"
                    ? "البلاغات سرّية ولا يطلع عليها صاحب الإعلان."
                    : "Reports are confidential and not shared with the seller."}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Phase 2A — Submit offer modal */}
      <SubmitOfferModal
        open={offerOpen}
        onClose={() => setOfferOpen(false)}
        vehicle={v}
        onSubmitted={loadOffers}
      />
    </div>
  );
}

// iter220 — description block with collapse / expand toggle so that
// even a full 500-char description does not push the rest of the ad
// off-screen. Nothing is ever hidden permanently; "عرض المزيد" simply
// reveals the full stored text without modification.
// iter222 — Fullscreen lightbox for the vehicle gallery.
//
// • Overlays the page with a black backdrop and a single centred image
//   at its natural aspect ratio (object-contain — never stretched).
// • Prev / Next buttons + keyboard arrows + horizontal swipe on touch
//   devices. Escape closes.
// • Photo counter is anchored at the TOP so it never sits on top of
//   the picture and never blocks the close (×) button.
// • Does NOT modify the underlying activeIdx logic — it delegates the
//   index to the parent so returning to the page keeps the same photo
//   selected.
function GalleryLightbox({ images, activeIdx, onIndexChange, onClose, lang }) {
  const count = images.length;
  const touch = React.useRef({ x: 0, active: false });

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") onIndexChange((activeIdx + 1) % count);
      else if (e.key === "ArrowLeft") onIndexChange((activeIdx - 1 + count) % count);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [activeIdx, count, onClose, onIndexChange]);

  const goNext = () => onIndexChange((activeIdx + 1) % count);
  const goPrev = () => onIndexChange((activeIdx - 1 + count) % count);

  const onTouchStart = (e) => {
    if (!e.touches[0]) return;
    touch.current = { x: e.touches[0].clientX, active: true };
  };
  const onTouchEnd = (e) => {
    if (!touch.current.active) return;
    const dx = (e.changedTouches[0]?.clientX ?? touch.current.x) - touch.current.x;
    touch.current.active = false;
    if (Math.abs(dx) < 40) return;
    // In RTL, swipe direction is inverted for user expectation.
    const rtl = document.documentElement.dir === "rtl";
    const nextDir = rtl ? dx > 0 : dx < 0;
    if (nextDir) goNext(); else goPrev();
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      data-testid="gallery-lightbox"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Top bar — counter left, close right. Respects the iOS safe-area
          inset so the buttons sit BELOW the notch / status bar icons
          (Dynamic Island, cellular / battery indicators). Padding is at
          least 12px on devices without a notch. Close-button hit area
          is 48×48px (min 44×44 iOS recommendation is met). */}
      <div
        className="absolute top-0 inset-x-0 flex items-center justify-between px-4 z-10"
        style={{
          paddingTop: "max(env(safe-area-inset-top), 12px)",
          paddingBottom: "12px",
        }}
      >
        <div
          className="text-white/95 text-sm font-semibold px-3 py-1 rounded-full bg-black/60 border border-white/20"
          data-testid="lightbox-counter"
          dir="ltr"
        >
          {activeIdx + 1} / {count}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
          style={{
            background: "linear-gradient(135deg,#e8c873,#c9a55c)",
            color: "#0a0a0a",
          }}
          aria-label={lang === "ar" ? "إغلاق المعرض" : "Close gallery"}
          data-testid="lightbox-close"
        >
          <CloseIcon className="w-7 h-7" strokeWidth={2.5} />
        </button>
      </div>

      {/* Image (aspect preserved via object-contain) */}
      <img
        src={images[activeIdx]}
        alt=""
        className="max-h-[90vh] max-w-[95vw] w-auto h-auto object-contain select-none"
        draggable={false}
        data-testid="lightbox-image"
      />

      {/* Prev / Next — only if there is more than one image */}
      {count > 1 && (
        <>
          <button
            type="button"
            onClick={goPrev}
            className="absolute top-1/2 -translate-y-1/2 start-3 w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center"
            aria-label="Previous"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-6 h-6 rtl:hidden" />
            <ChevronRight className="w-6 h-6 ltr:hidden" />
          </button>
          <button
            type="button"
            onClick={goNext}
            className="absolute top-1/2 -translate-y-1/2 end-3 w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center"
            aria-label="Next"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-6 h-6 rtl:hidden" />
            <ChevronLeft className="w-6 h-6 ltr:hidden" />
          </button>
        </>
      )}
    </div>
  );
}


function DescriptionBlock({ text, lang }) {
  const [expanded, setExpanded] = React.useState(false);
  const raw = (text || "").trim();
  if (!raw) {
    return (
      <p className="text-[#3a3a3a] leading-relaxed">
        {lang === "ar" ? "لا يوجد وصف." : "No description."}
      </p>
    );
  }
  // Collapse threshold: descriptions <= 220 chars OR that fit within 4
  // lines are shown in full and no toggle is rendered.
  const collapseThreshold = 220;
  const canCollapse = raw.length > collapseThreshold || raw.split("\n").length > 4;
  return (
    <div>
      <p
        className="text-[#3a3a3a] leading-relaxed whitespace-pre-wrap"
        style={
          !expanded && canCollapse
            ? {
                display: "-webkit-box",
                WebkitLineClamp: 4,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }
            : undefined
        }
        data-testid="vehicle-description-text"
      >
        {raw}
      </p>
      {canCollapse && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 text-xs font-semibold text-[#8a6f2e] hover:text-[#c9a55c] underline underline-offset-4 decoration-[#c9a55c]/50"
          data-testid="vehicle-description-toggle"
        >
          {expanded
            ? (lang === "ar" ? "عرض أقل" : "Show less")
            : (lang === "ar" ? "عرض المزيد" : "Show more")}
        </button>
      )}
    </div>
  );
}


function ActionCard({ icon: Icon, title, to, external = false }) {
  // Internal CARTAL service pages open in the SAME tab so the browser's
  // Back button (and our visual Back button) returns the user to this
  // vehicle page with their scroll/state preserved. External partner
  // links (e.g. Absher / government portals) open in a new tab.
  //
  // iter222 — reverted to the original pre-iter221 sizing. The cards
  // never actually changed size; they only *looked* smaller in the
  // 8-photo screenshot because a stray negative-margin on the thumbnail
  // strip was pushing the whole page wider than the viewport (fixed in
  // this iter). No transform:scale is used — dimensions are plain
  // Tailwind utilities.
  const className = "group flex items-center gap-3 p-4 rounded-xl bg-[#0a0a0a] hover:bg-[#1a1a1a] border border-[#c9a55c]/40 hover:border-[#c9a55c] transition-all";
  const style = { color: "#c9a55c" };
  const testId = `action-card-${String(to).replace(/\W+/g, "-")}`;
  const inner = (
    <>
      <div className="w-11 h-11 rounded-lg bg-[#c9a55c]/15 border border-[#c9a55c]/60 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5" strokeWidth={2.2} style={{ color: "#d4b56e" }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[15px] font-bold tracking-wide truncate" style={{ color: "#c9a55c" }}>{title}</div>
      </div>
      <ArrowRight className="w-4 h-4 rtl:rotate-180 transition-colors flex-shrink-0" style={{ color: "#c9a55c" }} />
    </>
  );
  if (external) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        style={style}
        data-testid={testId}
      >
        {inner}
      </a>
    );
  }
  return (
    <Link to={to} className={className} style={style} data-testid={testId}>
      {inner}
    </Link>
  );
}

function SellerTrustSummary({ sellerId, lang }) {
  const [t, setT] = useState(null);
  useEffect(() => {
    if (!sellerId) return;
    import("../lib/api").then(({ apiClient }) =>
      apiClient.get(`/sellers/${sellerId}/trust`)
        .then(({ data }) => setT(data))
        .catch(() => {})
    );
  }, [sellerId]);
  if (!t) return null;
  const hasReviews = t.rating_count > 0;
  return (
    <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="seller-trust-summary">
      <TrustScoreBadge score={t.trust_score} size="sm" lang={lang} showLabel={false} />
      {hasReviews ? (
        <StarRating value={t.rating_avg} count={t.rating_count} size="sm" lang={lang} />
      ) : (
        <span className="text-[10px] text-[#8a8a8a]">{lang === "ar" ? "بدون مراجعات بعد" : "No reviews yet"}</span>
      )}
    </div>
  );
}
